import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Logo from '../../../assets/images/brand/logo-black.png';
import {
  KEY_TOKEN_SIMULADAO,
} from '../../api/simuladaoAxios';
import { getLocalStorage, removeLocalStorage } from '../../commom/localStorage';
import { Button } from '../../components/atoms/Button';
import { useGetCurrentSimuladao } from '../../../modules/Simuladao/hooks/useGetCurrentSimuladao';
import { useSimuladaoStore } from '../../../modules/Simuladao/stores/simuladaoStore';
import { LoadingPage } from '../../components/atoms/LoadingPage';
import { IMainTemplate } from '../../models';
import { Typography } from '../../components/molecules/Typography';
import { Icon } from '../../components/atoms/Icon';
//import { GetCurrentSimuladaoResponseDto } from '../../../modules/Simuladao/models/getCurrentSimuladaoResponseDto';
import { isAfter } from 'date-fns';

type SimuladaoTokenPayload = {
  email?: string;
  name?: string;
  exp?: number;
};

function decodeSimuladaoToken(token: string | null): SimuladaoTokenPayload | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const parsed = JSON.parse(jsonPayload) as SimuladaoTokenPayload;
    if (parsed.exp && parsed.exp * 1000 < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

const SimuladaoTemplate: React.FC<IMainTemplate> = ({ children }) => {
  const { daySlug } = useParams();
  const [searchParams] = useSearchParams();
  const day = searchParams.get('day');

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const student = decodeSimuladaoToken(getLocalStorage(KEY_TOKEN_SIMULADAO));

  function handleLogout() {
    removeLocalStorage(KEY_TOKEN_SIMULADAO);
    navigate('/simuladao/login');
  }

  const {
    data: currentSimuladaoData,
    mutate: getCurrentSimuladao,
    isLoading,
    isError,
  } = useGetCurrentSimuladao();

  const { setCurrentSimuladao, currentSimuladao } = useSimuladaoStore();

  useEffect(() => {
    if (!pathname.includes('login') && !pathname.includes('inscricao')) {
      getCurrentSimuladao();
    }
  }, []);

  const feedbackPaths = [
    '/simuladao/gabarito',
    '/simuladao/gabarito/respostas/dia-01',
    '/simuladao/gabarito/respostas/dia-02',
    '/simuladao/gabarito/redacao/correcao',
    '/simuladao/gabarito/redacao/tema',
    '/simuladao/gabarito/redacao/aguardando',
    '/simuladao/ranking',
  ];

  const isSecondDay = day == '2';
  const { isFinalScoreReleased, isParcialScoreReleased } = useMemo(() => {
    if (!currentSimuladaoData)
      return {
        isFinalScoreReleased: false,
        isParcialScoreReleased: false,
      };

    if (
      isSecondDay &&
      currentSimuladaoData.finalScoreReleasedAt &&
      currentSimuladaoData.rankingStartDateSecondDay
    ) {
      return {
        isFinalScoreReleased:
          new Date() > new Date(currentSimuladaoData.finalScoreReleasedAt),
        isParcialScoreReleased:
          new Date() > new Date(currentSimuladaoData.rankingStartDateSecondDay),
      };
    }

    return {
      isFinalScoreReleased:
        new Date() >
        new Date(
          currentSimuladaoData.finalScoreReleasedAt
            ? currentSimuladaoData.finalScoreReleasedAt
            : currentSimuladaoData.rankingStartDate
        ),
      isParcialScoreReleased:
        new Date() > new Date(currentSimuladaoData.rankingStartDate),
    };
  }, [currentSimuladaoData]);

  useEffect(() => {
    if (currentSimuladaoData && currentSimuladaoData !== currentSimuladao) {
      if (!currentSimuladaoData.isMatriculated) {
        // Todo Página de inscrição do simuladão
        window.open('/simuladao/login', '_self');
        return;
      }

      const simulationHasStarted =
        currentSimuladao?.isStarted || currentSimuladao?.isStartedSecondDay;

      /* const { userHasFinished } =
        getCurrentDaySimulationStatus(currentSimuladaoData); */

      if (pathname === '/simuladao' && !day) {
        window.open('/simuladao/portao', '_self');
        return;
      }

      const redirectToSisu =
        !isSecondDay &&
        !simulationHasStarted &&
        !currentSimuladaoData.hasAddedSisuData && !isAfter(new Date(), new Date('2025-08-29 10:00:00'));

      if (redirectToSisu) {
        if (pathname !== '/simuladao/sisu') {
          window.open('/simuladao/sisu', '_self');
        } else {
          setCurrentSimuladao(currentSimuladaoData);
        }
        return;
      }

      const feedbackPathsWithoutGabarito = feedbackPaths.filter(
        (path) => path !== '/simuladao/gabarito'
      );

      if (
        feedbackPathsWithoutGabarito.includes(pathname) &&
        !isFinalScoreReleased &&
        !isParcialScoreReleased
      ) {
        const day = !daySlug || daySlug === 'dia-01' ? 1 : 2;

        window.open(`/simuladao/gabarito/${currentSimuladaoData.slug}/${day}`, '_self');
        return;
      }

      setCurrentSimuladao(currentSimuladaoData);
    }
  }, [currentSimuladaoData]);

  const shouldShowError = (isError) 

  if (pathname.includes('login') || pathname.includes('inscricao')) {
    return <>{children}</>;
  }

  return (
    <div className='flex min-h-screen flex-col'>
      <div className='fixed top-0 z-10 flex h-16 w-full items-center bg-mono-color-white shadow-level-1'>
        <div className='mx-auto flex w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8'>
          <img
            className=''
            src={Logo}
            alt='Logo'
            width='138.09px'
            height='24px'
          />

          {student?.email && (
            <div className='flex items-center gap-4'>
              <div className='hidden items-center gap-2 rounded-lg bg-color-lightest px-4 py-2 md:flex'>
                <Icon
                  type='material'
                  Icon='person'
                  className='text-brand-color-primary-pure'
                />
                <div className='flex flex-col'>
                  {student.name && (
                    <Typography
                      size='small'
                      weight='bold'
                      className='text-mono-color-dark'
                    >
                      {student.name}
                    </Typography>
                  )}
                  <Typography size='small' className='text-mono-color-dark'>
                    {student.email}
                  </Typography>
                </div>
              </div>

              <Button
                variant='outlined'
                size='small'
                onClick={handleLogout}
                className='flex items-center gap-2'
              >
                <Icon type='material' Icon='logout' className='text-sm' />
                <span className='hidden sm:inline'>Sair</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <div 
        className={`
          mt-16 min-h-[calc(100vh-4rem)] w-full bg-color-lighter 
          ${pathname.match(/\/simuladao\/gabarito\/[^/]+\/[12]$/) ? 'pb-0' : 'pb-[110px]'} md:pb-0
        `}
      >
        {shouldShowError ? (
          <div
            className={
              'mt-[10vh] flex flex-col items-center justify-center gap-md'
            }
          >
            <Icon
              Icon={'Error'}
              type={'material'}
              className={'text-5xl text-feedback-color-error-pure'}
            />
            <Typography
              type={'title'}
              size={'h3'}
              className={'text-center text-feedback-color-error-pure'}
            >
              Ops, simuladão não encontrado.
            </Typography>
          </div>
        ) : !currentSimuladao || isLoading ? (
          <LoadingPage label='Carregando dados do simuladão...' />
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export default SimuladaoTemplate;
